// ============================================
// TestResults
// Test run results viewer with readiness gate
// Shows pass rates, filtering, and launch gate status
// ============================================

import { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList,
  CheckCircle2,
  Filter,
  Download,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { getTestRuns, getTestStats } from '../../../services/firestore/pjeService';
import type { TestRun as FirestoreTestRun } from '../../../types/patientJourney';
import {
  assessLaunchReadiness,
  type LaunchGateResult,
  type ArchetypeGateResult,
} from '../../../services/patientJourney/launchGate';

// ── Types ──

type RunStatus = 'completed' | 'failed' | 'in_progress' | 'timed_out';

interface DisplayTestRun {
  runId: string;
  persona: string;
  touchpoint: string;
  status: RunStatus;
  passRate: number;
  scorer: 'manual' | 'ai_twin';
  date: string;
  suiteId: string;
}

// ── Status Config ──

const STATUS_STYLES: Record<RunStatus, { color: string; bgColor: string; label: string }> = {
  completed: { color: 'text-emerald-400', bgColor: 'bg-emerald-400/10', label: 'Completed' },
  failed: { color: 'text-critical', bgColor: 'bg-critical/10', label: 'Failed' },
  in_progress: { color: 'text-gold', bgColor: 'bg-gold/10', label: 'In Progress' },
  timed_out: { color: 'text-text-muted', bgColor: 'bg-bg-elevated', label: 'Timed Out' },
};

// ── Filter Options ──

const SUITE_OPTIONS = [
  { value: 'all', label: 'All Suites' },
  { value: 'standard_journey', label: 'Standard Journey' },
  { value: 'edge_cases', label: 'Edge Cases' },
  { value: 'regression', label: 'Regression' },
];

const PERSONA_OPTIONS = [
  { value: 'all', label: 'All Personas' },
  { value: 'clean_gate1', label: 'Clean Gate 1' },
  { value: 'drug_interaction', label: 'Drug Interaction' },
  { value: 'gate2_escalation', label: 'Gate 2 Escalation' },
  { value: 'state_restriction', label: 'State Restriction' },
  { value: 'multi_protocol', label: 'Multi-Protocol' },
  { value: 'exclusion', label: 'Exclusion' },
  { value: 'skeptic', label: 'Skeptic' },
  { value: 'over_optimizer', label: 'Over-Optimizer' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'in_progress', label: 'In Progress' },
];

const SCORER_OPTIONS = [
  { value: 'all', label: 'All Scorers' },
  { value: 'manual', label: 'Manual' },
  { value: 'ai_twin', label: 'AI Twin' },
];

// ── Component ──

export function TestResults() {
  const [testRuns, setTestRuns] = useState<DisplayTestRun[]>([]);
  const [stats, setStats] = useState<{ totalRuns: number; passRate: number; lastRunAt: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterSuite, setFilterSuite] = useState('all');
  const [filterPersona, setFilterPersona] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterScorer, setFilterScorer] = useState('all');
  const [gateResult, setGateResult] = useState<LaunchGateResult | null>(null);

  /** Map Firestore TestRun to display format */
  function toDisplayRun(run: FirestoreTestRun): DisplayTestRun {
    return {
      runId: run.runId,
      persona: run.personaId,
      touchpoint: run.touchpointId,
      status: run.status as RunStatus,
      passRate: run.passRate ?? 0,
      scorer: run.scorer,
      date: run.startedAt ? new Date(run.startedAt).toLocaleDateString() : '--',
      suiteId: run.suiteId ?? '',
    };
  }

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters: { personaId?: string; status?: string; suiteId?: string } = {};
      if (filterPersona !== 'all') filters.personaId = filterPersona;
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterSuite !== 'all') filters.suiteId = filterSuite;

      const [runs, statsData] = await Promise.all([
        getTestRuns(Object.keys(filters).length > 0 ? filters : undefined),
        getTestStats(),
      ]);
      setTestRuns(runs.map(toDisplayRun));
      setStats(statsData);

      // Assess launch gate from actual runs
      if (runs.length > 0) {
        const gate = assessLaunchReadiness(runs, 'standalone', 'hemologix');
        setGateResult(gate);
      }
    } catch (err) {
      console.error('Failed to load test data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterPersona, filterStatus, filterSuite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Compute summary stats from state
  const totalRuns = stats?.totalRuns ?? 0;
  const passRate = stats && stats.totalRuns > 0 ? Math.round(stats.passRate) : null;
  const lastRunDisplay = stats?.lastRunAt
    ? new Date(stats.lastRunAt).toLocaleDateString()
    : 'Never';

  // Client-side filter for scorer (not a Firestore filter)
  const filteredResults = testRuns.filter((r) => {
    if (filterScorer !== 'all' && r.scorer !== filterScorer) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-display text-gold flex items-center gap-2">
            <ClipboardList size={20} />
            Test Results
          </h3>
          <p className="text-base text-text-secondary mt-1">
            Readiness score:{' '}
            <span className="text-text-primary font-semibold">
              {passRate !== null ? `${passRate}%` : '--%'}
            </span>
          </p>
        </div>
        <button
          disabled={testRuns.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-secondary hover:text-text-primary hover:border-gold/30 transition-colors text-base disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={16} />
          Export Results CSV
        </button>
      </div>

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total Runs" value={totalRuns > 0 ? String(totalRuns) : '0'} />
        <SummaryCard
          label="Pass Rate"
          value={passRate !== null ? `${passRate}%` : '--%'}
          highlight={passRate !== null && passRate >= 85}
        />
        <SummaryCard
          label="Readiness Score"
          value={passRate !== null ? `${passRate}%` : '--%'}
          highlight={passRate !== null && passRate >= 85}
        />
        <SummaryCard label="Last Run" value={lastRunDisplay} />
      </div>

      {/* ── Filters ── */}
      <div
        className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
          <Filter size={16} className="text-text-muted" />
          <span className="text-base font-semibold font-display text-gold">
            Filters
          </span>
        </div>

        <div className="px-5 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm text-text-muted mb-1">Suite</label>
            <select
              value={filterSuite}
              onChange={(e) => setFilterSuite(e.target.value)}
              className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
            >
              {SUITE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">Persona</label>
            <select
              value={filterPersona}
              onChange={(e) => setFilterPersona(e.target.value)}
              className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
            >
              {PERSONA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">Scorer</label>
            <select
              value={filterScorer}
              onChange={(e) => setFilterScorer(e.target.value)}
              className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
            >
              {SCORER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Results Table ── */}
      <div
        className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
          <BarChart3 size={16} className="text-blue-400" />
          <span className="text-base font-semibold font-display text-gold">
            Results
          </span>
          {!loading && totalRuns > 0 && (
            <span className="text-sm text-text-muted ml-2">
              {filteredResults.length} of {totalRuns} runs
            </span>
          )}
        </div>

        {loading ? (
          <div className="px-5 py-12 flex flex-col items-center justify-center text-center">
            <Loader2 size={32} className="text-gold animate-spin mb-3" />
            <p className="text-base text-text-muted">Loading test results...</p>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="px-5 py-12 flex flex-col items-center justify-center text-center">
            <ClipboardList size={40} className="text-text-muted/30 mb-3" />
            <p className="text-base text-text-muted">
              No test runs yet.
            </p>
            <p className="text-base text-text-muted mt-1">
              Run an AI Twin or manually score a persona to see results here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead>
                <tr className="border-b border-bg-tertiary">
                  <th className="text-left py-3 px-5 text-text-muted font-medium text-sm">
                    Run ID
                  </th>
                  <th className="text-left py-3 px-3 text-text-muted font-medium text-sm">
                    Persona
                  </th>
                  <th className="text-left py-3 px-3 text-text-muted font-medium text-sm">
                    Touchpoint
                  </th>
                  <th className="text-left py-3 px-3 text-text-muted font-medium text-sm">
                    Status
                  </th>
                  <th className="text-right py-3 px-3 text-text-muted font-medium text-sm">
                    Pass Rate
                  </th>
                  <th className="text-left py-3 px-3 text-text-muted font-medium text-sm">
                    Scorer
                  </th>
                  <th className="text-left py-3 px-5 text-text-muted font-medium text-sm">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((run) => {
                  const statusStyle = STATUS_STYLES[run.status];
                  return (
                    <tr
                      key={run.runId}
                      className="border-b border-bg-tertiary/50 last:border-0 hover:bg-bg-elevated/30 transition-colors"
                    >
                      <td className="py-3 px-5 font-mono text-sm text-text-muted">
                        {run.runId}
                      </td>
                      <td className="py-3 px-3 text-text-primary">{run.persona}</td>
                      <td className="py-3 px-3 text-text-secondary">{run.touchpoint}</td>
                      <td className="py-3 px-3">
                        <span
                          className={`text-sm font-semibold px-2 py-0.5 rounded-lg ${statusStyle.color} ${statusStyle.bgColor}`}
                        >
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span
                          className={`font-semibold ${
                            run.passRate >= 85
                              ? 'text-emerald-400'
                              : run.passRate >= 70
                              ? 'text-amber-400'
                              : 'text-critical'
                          }`}
                        >
                          {run.passRate}%
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`text-sm px-2 py-0.5 rounded-lg ${
                            run.scorer === 'ai_twin'
                              ? 'text-purple-400 bg-purple-400/10'
                              : 'text-blue-400 bg-blue-400/10'
                          }`}
                        >
                          {run.scorer === 'ai_twin' ? 'AI Twin' : 'Manual'}
                        </span>
                      </td>
                      <td className="py-3 px-5 text-text-muted text-sm">{run.date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Readiness Gate ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-gold" />
          <span className="text-base font-semibold font-display text-gold">
            Launch Gate
          </span>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Overall verdict */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base text-text-secondary">Overall Verdict</div>
              <div className="text-sm text-text-muted mt-0.5">
                {gateResult
                  ? `${gateResult.overallPassRate.toFixed(1)}% pass rate (${gateResult.threshold}% required)`
                  : 'Run tests to assess readiness'}
              </div>
            </div>
            <span
              className={`text-base font-bold px-4 py-2 rounded-xl ${
                gateResult?.ready
                  ? 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/30'
                  : gateResult
                  ? 'text-critical bg-critical/10 border border-critical/30'
                  : 'text-amber-400 bg-amber-400/10 border border-amber-400/30'
              }`}
            >
              {gateResult?.ready ? 'READY TO LAUNCH' : gateResult ? 'NOT READY' : 'PENDING'}
            </span>
          </div>

          {/* Per-archetype breakdown */}
          {gateResult && gateResult.archetypeResults.length > 0 && (
            <div className="border-t border-bg-tertiary pt-4">
              <div className="text-sm font-semibold text-text-secondary mb-3">
                Per-Archetype Results
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {gateResult.archetypeResults.map((ar: ArchetypeGateResult) => (
                  <div
                    key={ar.archetypeId}
                    className="bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm text-text-primary">{ar.archetypeName || ar.archetypeId}</div>
                      <div className="text-xs text-text-muted">{ar.runCount} run{ar.runCount !== 1 ? 's' : ''}</div>
                    </div>
                    <span
                      className={`text-sm font-semibold px-2 py-0.5 rounded ${
                        ar.passed
                          ? 'text-emerald-400 bg-emerald-400/10'
                          : 'text-critical bg-critical/10'
                      }`}
                    >
                      {ar.passRate.toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blocking issues */}
          {gateResult && gateResult.blockingIssues.length > 0 && (
            <div className="border-t border-bg-tertiary pt-4">
              <div className="text-sm font-semibold text-critical mb-2">
                Blocking Issues ({gateResult.blockingIssues.length})
              </div>
              <div className="space-y-1">
                {gateResult.blockingIssues.map((issue, i) => (
                  <div key={i} className="text-sm text-text-secondary bg-critical/5 border border-critical/20 rounded-lg px-3 py-2">
                    {issue}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {gateResult && gateResult.warnings.length > 0 && (
            <div className="border-t border-bg-tertiary pt-4">
              <div className="text-sm font-semibold text-amber-400 mb-2">
                Warnings ({gateResult.warnings.length})
              </div>
              <div className="space-y-1">
                {gateResult.warnings.map((warning, i) => (
                  <div key={i} className="text-sm text-text-secondary bg-amber-400/5 border border-amber-400/20 rounded-lg px-3 py-2">
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm text-text-muted border-t border-bg-tertiary pt-3">
            All archetypes must pass at 75%+ and overall rate must reach 85% before brand can go live
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl px-4 py-4 text-center">
      <div
        className={`text-2xl font-bold ${
          highlight ? 'text-emerald-400' : 'text-text-muted'
        }`}
      >
        {value}
      </div>
      <div className="text-sm text-text-muted mt-1">{label}</div>
    </div>
  );
}
